'use client';

import { mdiCheckCircle, mdiCircleOutline, mdiInformationOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { useId, useState } from 'react';

import { checkPasswordPolicy, PASSWORD_ALLOWED_SYMBOLS_DISPLAY, PASSWORD_MIN_LENGTH } from '@/lib/auth/password-policy';

type Props = {
  /** 入力中のパスワード。項目ごとに満たしているかを判定して表示に反映する */
  password: string;
  /** 幅の指定など。入力欄と同じ幅にしておくと、記号一覧を開いてもレイアウトが動かない */
  className?: string;
};

/**
 * パスワード入力欄の直下に出すポリシーのチェックリスト。
 * 4項目を横並びのチップで示し、入力に応じて満たした項目にチェックが付く。
 * 「記号」の ⓘ を押すと、使用できる記号の一覧がその下に開く。
 */
export default function PasswordPolicyChecklist({ password, className = '' }: Props) {
  const [isSymbolListOpen, setIsSymbolListOpen] = useState(false);
  const symbolListId = useId();
  const check = checkPasswordPolicy(password);

  const items = [
    { key: 'minLength', label: `${PASSWORD_MIN_LENGTH}文字以上`, met: check.minLength },
    { key: 'upperCase', label: '大文字', met: check.upperCase },
    { key: 'lowerCase', label: '小文字', met: check.lowerCase },
    { key: 'symbol', label: '記号', met: check.symbol },
  ] as const;

  return (
    <div className={`flex flex-col gap-1 text-xs ${className}`}>
      <ul className="flex flex-wrap gap-x-3 gap-y-1" aria-label="パスワードの条件">
        {items.map(item => (
          <li
            key={item.key}
            className={`flex items-center gap-1 ${item.met ? 'text-success' : 'text-gray-500'}`}
            aria-label={`${item.label}: ${item.met ? '満たしています' : '未入力'}`}
          >
            <Icon path={item.met ? mdiCheckCircle : mdiCircleOutline} size={0.6} aria-hidden />
            <span>{item.label}</span>
            {item.key === 'symbol' && (
              <button
                type="button"
                className="flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
                aria-label="使用できる記号を表示"
                aria-expanded={isSymbolListOpen}
                aria-controls={symbolListId}
                onClick={() => setIsSymbolListOpen(open => !open)}
              >
                <Icon path={mdiInformationOutline} size={0.6} aria-hidden />
              </button>
            )}
          </li>
        ))}
      </ul>
      {isSymbolListOpen && (
        <p id={symbolListId} className="text-gray-600">
          使用できる記号:{' '}
          <span className="font-mono whitespace-pre-wrap break-all text-10px tracking-wider">
            {PASSWORD_ALLOWED_SYMBOLS_DISPLAY}
          </span>
        </p>
      )}
    </div>
  );
}
